<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import ReportHeader from './components/ReportHeader.vue';
import BroadcastCard from './components/broadcasts/BroadcastCard.vue';
import BroadcastComposer from './components/broadcasts/BroadcastComposer.vue';
import BroadcastBlacklist from './components/broadcasts/BroadcastBlacklist.vue';
import { useBroadcasts } from '../../../../composables/useBroadcasts';
import Spinner from '../../../../../shared/components/Spinner.vue';
import { formatDateTime } from '../../../../helper/dateHelper';

const { t } = useI18n();
const store = useStore();
const isComposerOpen = ref(false);
const blacklistVersion = ref(0);
const inboxes = useMapGetter('inboxes/getInboxes');
const teams = useMapGetter('teams/getTeams');

const telegramInboxes = computed(() =>
  inboxes.value.filter(inbox => inbox.channel_type === 'Channel::Telegram')
);

const {
  activeBroadcasts,
  historyBroadcasts,
  isLoading,
  lastUpdateTime,
  fetchBroadcasts,
} = useBroadcasts();

onMounted(() => {
  fetchBroadcasts();
  store.dispatch('inboxes/get');
  store.dispatch('teams/get');
});

const handleBroadcastCreated = () => {
  isComposerOpen.value = false;
  fetchBroadcasts();
};
</script>

<template>
  <ReportHeader
    :header-title="t('BROADCASTS.TITLE')"
    :header-description="t('BROADCASTS.DESCRIPTION')"
  >
    <Button
      icon="i-lucide-megaphone"
      :label="t('BROADCASTS.COMPOSER.CREATE')"
      :disabled="isComposerOpen"
      @click="isComposerOpen = true"
    />
  </ReportHeader>

  <BroadcastBlacklist @changed="blacklistVersion += 1" />

  <BroadcastComposer
    v-if="isComposerOpen"
    :telegram-inboxes="telegramInboxes"
    :teams="teams"
    :blacklist-version="blacklistVersion"
    @close="isComposerOpen = false"
    @created="handleBroadcastCreated"
  />

  <div
    class="flex items-center justify-between mb-6 p-4 bg-n-alpha-1 rounded-lg border border-n-strong"
  >
    <div class="text-sm text-n-slate-11">
      <span v-if="lastUpdateTime">
        {{ t('BROADCASTS.LAST_UPDATE') }}
        {{ formatDateTime(lastUpdateTime, 'ru-RU') }}
      </span>
      <span v-else>
        {{ t('BROADCASTS.LOADING') }}
      </span>
    </div>
    <button
      :disabled="isLoading"
      class="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-n-slate-12 bg-n-brand hover:bg-n-brand-dark disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors"
      @click="fetchBroadcasts"
    >
      <Spinner v-if="isLoading" size="16" />
      <svg
        v-else
        class="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
        />
      </svg>
      {{ isLoading ? t('BROADCASTS.REFRESHING') : t('BROADCASTS.REFRESH') }}
    </button>
  </div>

  <div class="flex flex-col flex-1 gap-8 mt-6">
    <div v-if="activeBroadcasts.length > 0" class="flex flex-col gap-4">
      <h2 class="text-lg font-medium text-n-slate-12">
        {{ t('BROADCASTS.ACTIVE_BROADCASTS') }}
      </h2>
      <div class="flex flex-col gap-4">
        <BroadcastCard
          v-for="broadcast in activeBroadcasts"
          :key="broadcast.id"
          :broadcast="broadcast"
        />
      </div>
    </div>

    <div
      v-if="activeBroadcasts.length && historyBroadcasts.length"
      class="w-full h-px bg-n-strong"
    />

    <div class="flex flex-col gap-4">
      <h2 class="text-lg font-medium text-n-slate-12">
        {{ t('BROADCASTS.HISTORY') }}
      </h2>

      <div v-if="isLoading" class="flex justify-center items-center py-8">
        <Spinner size="30" />
      </div>

      <div v-else-if="historyBroadcasts.length > 0" class="flex flex-col gap-4">
        <BroadcastCard
          v-for="broadcast in historyBroadcasts"
          :key="broadcast.id"
          :broadcast="broadcast"
        />
      </div>

      <div
        v-else
        class="text-n-slate-11 py-5 text-center bg-n-alpha-2 rounded-xl border border-n-strong border-dashed"
      >
        {{ t('BROADCASTS.NO_COMPLETED_BROADCASTS') }}
      </div>
    </div>
  </div>
</template>
